import PhoneNumberInputWithCountry, {
  type Country,
  type Value,
} from "react-phone-number-input";
import "react-phone-number-input/style.css";
import styled, { css } from "styled-components";
import { ColorV2 } from "config/colors";
import { TypographyV2 } from "config/font";
import {
  V2_INPUT_HEIGHT_PX,
  V2_INPUT_WIDTH_PX,
} from "components/designV2/constants";

export type PhoneInputState = "inactive" | "disabled" | "active" | "error";

const DEFAULT_COUNTRY: Country = "SE";

export interface PhoneInputProps {
  label?: string;
  hintText?: string;
  errorText?: string;
  hasError?: boolean;
  disabled?: boolean;
  fullWidth?: boolean;
  isHighlighted?: boolean;
  placeholder?: string;
  /** Overrides the country selected before the user picks one themselves. */
  defaultCountry?: Country;
  /** E.164, for example `+46701234567`. */
  value: string;
  /** Receives E.164, or an empty string once the field is cleared. */
  onChange: (phoneNumber: string) => void;
  onBlur?: () => void;
  onFocus?: () => void;
}

/**
 * Phone number field with a country selector, wrapping
 * `react-phone-number-input` so it carries the design system's chrome.
 */
export const PhoneInput = (props: PhoneInputProps): JSX.Element => {
  const visualState: PhoneInputState = props.disabled
    ? "disabled"
    : props.hasError
      ? "error"
      : "inactive";
  const message = props.hasError ? props.errorText : props.hintText;

  return (
    <Container $fullWidth={props.fullWidth ?? false}>
      {props.label && <Label>{props.label}</Label>}
      <FieldWrapper $state={visualState} $isHighlighted={props.isHighlighted}>
        <PhoneNumberInputWithCountry
          international
          withCountryCallingCode
          defaultCountry={props.defaultCountry ?? DEFAULT_COUNTRY}
          value={props.value.length === 0 ? undefined : (props.value as Value)}
          placeholder={props.placeholder}
          disabled={props.disabled}
          onChange={(nextValue: Value | undefined) => {
            props.onChange(nextValue ?? "");
          }}
          onBlur={props.onBlur}
          onFocus={props.onFocus}
        />
      </FieldWrapper>
      {message && <Message $state={visualState}>{message}</Message>}
    </Container>
  );
};

const Container = styled.div<{ $fullWidth: boolean }>`
  width: ${(props) => (props.$fullWidth ? "100%" : `${V2_INPUT_WIDTH_PX}px`)};
  max-width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.span`
  ${TypographyV2.b2}
  color: ${ColorV2.Blue4};
`;

// Mirrors the InputField chrome. The package renders its own markup, so the
// border and height live here rather than on the input itself, and its classes
// are reset to sit inside them.
const FieldWrapper = styled.div<{
  $state: PhoneInputState;
  $isHighlighted?: boolean;
}>`
  display: flex;
  align-items: center;
  height: ${V2_INPUT_HEIGHT_PX}px;
  padding: 0 16px;
  border-radius: 16px;
  background-color: ${ColorV2.White};
  border: 1.5px solid ${ColorV2.Blue5};
  transition:
    border-color 0.2s ease,
    background-color 0.2s ease;

  &:focus-within {
    border-color: ${ColorV2.Blue1};
  }

  ${(props) =>
    props.$isHighlighted &&
    css`
      border-color: ${ColorV2.ElectricBlue};
    `}

  ${(props) =>
    props.$state === "error" &&
    css`
      border-color: ${ColorV2.NegativeRed};
    `}

  ${(props) =>
    props.$state === "disabled" &&
    css`
      border-color: ${ColorV2.Blue6};
      background-color: ${ColorV2.Blue7};
    `}

  .PhoneInput {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
  }

  .PhoneInputInput {
    ${TypographyV2.b2}
    flex: 1;
    min-width: 0;
    border: none;
    outline: none;
    background: transparent;
    color: ${ColorV2.Blue1};

    &::placeholder {
      color: ${ColorV2.Blue5};
    }

    &:disabled {
      color: ${ColorV2.Blue5};
    }
  }

  .PhoneInputCountry {
    margin: 0;
  }

  .PhoneInputCountrySelectArrow {
    color: ${ColorV2.Blue5};
    opacity: 1;
  }
`;

const Message = styled.span<{ $state: PhoneInputState }>`
  ${TypographyV2.s1}
  color: ${(props) =>
    props.$state === "error" ? ColorV2.NegativeRed : ColorV2.Blue4};
`;
